import React from "react";

import { AsyncComponent2 } from "@impero-com/common/components/asyncComponent";
import {
    ColumnBuilder,
    DataTable,
    DATA_TABLE_MODE_VIEWPORT,
} from "@impero-com/common/components/dataTable";
import * as dataTableTheme from "@impero-com/common/components/dataTable/theme.css";
import { TableFilter } from "@impero-com/common/components/filter";
import { PaginatedTable } from "@impero-com/common/components/table/paginatedTable";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import createIcon from "@impero-com/common/img/create-icon.svg";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import * as Cmp from "@impero-com/common/utils/comparison";
import {
    DataView,
    ArrayDataSource,
    DataExtractor,
    getAllExtractors,
} from "@impero-com/common/utils/dataView";
import * as DV from "@impero-com/common/utils/dataView";
import { toArray } from "@impero-com/common/utils/dataView/source";
import withI18n, { t } from "@impero-com/common/utils/i18n";
import {
    DataViewIndex,
    QueryResult,
    SimpleFieldFilter,
    SimpleAndFilter,
    addFieldFilter,
    removeFieldFilter,
} from "@impero-com/common/utils/indexing";
import {
    ContentWithoutBorder,
    renderMarkdown,
    NoContent,
    ProgressBar,
    Tooltip,
    PageSpinner,
} from "@impero-com/ui";
import { memoize } from "lodash-es";
import memoizeOne from "memoize-one";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { getEntities } from "api/generated/entities/entities";
import {
    EntityAssignment,
    SimpleRiskDirectory,
    SimpleRiskDirectoryDtoContent as RiskDirDto,
    SimpleRiskDirectoryListDto,
    AvailableActions,
    EntityOverviewListDto,
    EntityOverview,
} from "api/generated/model";
import { getRisks } from "api/generated/risks/risks";
import { HashSwitch } from "org/router";
import { FormCreate, FormRootComponentProps } from "utils/form";
import { useAvailableActions } from "utils/hooks/useAvailableActions";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";

import { OrgApplicationBar } from "../../../components/applicationBar/orgApplicationBar";
import { WebRoute } from "../../../routes";
import { RISK_MAP_HASH, RISK_DIRECTORY_HASH } from "../content";
import {
    NEW_RISK_DIRECTORY,
    RISK_MODULE,
    NEW_ENTITY,
    RISK_DIRECTORY_STRUCTURE,
} from "../url";

import { EntityManagementNotice } from "./EntityManagementNotice";
import * as theme from "./theme.css";

const userCanCreateRiskDirectory = (availableActions: AvailableActions) =>
    availableActions.risk.riskDirectory.create;

const userCanCreateEntity = (availableActions: AvailableActions) =>
    availableActions.risk.entity.create;

const OVERVIEW_TAB = "OVERVIEW";
const ENTITIES_TAB = "ENTITIES";

const FIXED_WIDTH_WIDE = 200;
const FIXED_WIDTH_EXTRA_WIDE = 250;
const FIXED_WIDTH_NARROW = 150;

export const ENTITIES_HASH = "#risk-entities";

type Tab = typeof OVERVIEW_TAB | typeof ENTITIES_TAB;

interface AppBarProps {
    ajaxManager: AjaxManager;
}

interface RiskManagerFormProps
    extends FormRootComponentProps<object>, AppBarProps {}

function RiskManagerForm(props: RiskManagerFormProps) {
    const { ajaxManager } = props;
    const availableActions = useAvailableActions();
    const controlEntitiesEnabled = useFeatureFlag("controlEntities");
    const riskEntitiesEnabled = useFeatureFlag("riskEntities");
    return (
        <AsyncComponent2<SimpleRiskDirectoryListDto, EntityOverviewListDto>
            ajaxManager={ajaxManager}
            key="risk-manager"
            promises={[() => getRisks(), () => getEntities({ scope: "risk" })]}
            renderLoading={() => <PageSpinner />}
            renderError={(code) => <LoggedInHTTPError code={code} />}
            renderContent={(riskDirectories, entities) => (
                <HashSwitch
                    hashRoutes={{
                        "": (
                            <RiskManagerOverview
                                riskDirectories={riskDirectories}
                                entities={entities}
                                ajaxManager={ajaxManager}
                                availableActions={availableActions}
                            />
                        ),
                        [ENTITIES_HASH]: riskEntitiesEnabled ? (
                            <RiskManagerEntities
                                entities={entities}
                                ajaxManager={ajaxManager}
                                availableActions={availableActions}
                                showEntityManagementNotice={
                                    controlEntitiesEnabled
                                }
                            />
                        ) : (
                            <Navigate to={WebRoute.ENTITIES} replace />
                        ),
                    }}
                />
            )}
        />
    );
}

interface RiskManagerApplicationBarProps extends AppBarProps {
    currentTabId: Tab;
    children: React.ReactNode;
}

function RiskManagerApplicationBar(props: RiskManagerApplicationBarProps) {
    const { currentTabId, children } = props;
    const availableActions = useAvailableActions();
    const riskEntitiesEnabled = useFeatureFlag("riskEntities");

    const navigate = useNavigate();

    const actions = {
        [OVERVIEW_TAB]: () => {
            return {
                id: "menuitem-1",
                label: t("RiskDirectory_Creator_Title"),
                icon: { svg: createIcon },
                link: { to: NEW_RISK_DIRECTORY },
                disabled: !userCanCreateRiskDirectory(availableActions),
            };
        },
        [ENTITIES_TAB]: () => {
            return {
                id: "menuitem-1",
                label: t("RiskEntities_Create_Title"),
                icon: { svg: createIcon },
                link: { to: NEW_ENTITY },
                disabled: !userCanCreateEntity(availableActions),
            };
        },
    };

    return (
        <OrgApplicationBar
            title={{
                title: t("RiskDirectory_RiskManager"),
                breadCrumbs: [],
            }}
            navigation={{
                type: "Tabs",
                currentTabId,
                tabSpecs: [
                    {
                        id: OVERVIEW_TAB,
                        label: t("RiskManager_Overview"),
                        onClick: () => {
                            navigate("#");
                        },
                    },
                    ...(riskEntitiesEnabled
                        ? [
                              {
                                  id: ENTITIES_TAB,
                                  label: t("RiskEntities_Title"),
                                  onClick: () => {
                                      navigate(ENTITIES_HASH);
                                  },
                              },
                          ]
                        : []),
                ],
            }}
            actions={{
                id: "action-menu",
                actions: [actions[currentTabId]()],
            }}
        >
            {children}
        </OrgApplicationBar>
    );
}

interface RiskManagerOverviewProps {
    riskDirectories: SimpleRiskDirectoryListDto;
    entities: EntityOverviewListDto;
    ajaxManager: AjaxManager;
    availableActions: AvailableActions;
}

interface RiskManagerOverviewState {
    filter: SimpleAndFilter | null;
}
interface ExpandedRow<E> {
    dataView: DV.DataView<E>;
    columnBuilders: { [extractorId: string]: ColumnBuilder<E, any> };
}

interface EntityAssignmentView {
    id: string;
    entityName: string;
    assessedRisks: number;
    risks: number;
}

class RiskManagerOverview extends React.PureComponent<
    RiskManagerOverviewProps,
    RiskManagerOverviewState
> {
    state: RiskManagerOverviewState = {
        filter: null,
    };
    render = () => {
        const { ajaxManager } = this.props;
        return (
            <RiskManagerApplicationBar
                currentTabId={OVERVIEW_TAB}
                ajaxManager={ajaxManager}
            >
                {this.renderContent()}
            </RiskManagerApplicationBar>
        );
    };

    private prepareEntities = memoize(
        (
            entities: Record<
                number,
                EntityAssignment
            > /* XXX If you add arguments, please define a memoization strategy */
        ): EntityAssignmentView[] => {
            const entityViews = Object.entries(entities).map(
                ([id, entity]) => ({
                    id,
                    entityName:
                        this.props.riskDirectories.linkedResources.entities.find(
                            (e) => e.id.toString() === id
                        )!.name,
                    assessedRisks: entity.assessedRisks,
                    risks: entity.risks,
                })
            );
            entityViews.sort(
                Cmp.map((entityView) => entityView.entityName, Cmp.string())
            );
            return entityViews;
        }
    );

    private expandRow = memoize(
        (
            item: SimpleRiskDirectory /* XXX If you add arguments, please define a memoization strategy */
        ): ExpandedRow<EntityAssignmentView> => {
            const entities = this.prepareEntities(item.entities);
            const getEntityStatus: (
                entity: EntityAssignmentView
            ) => number | null = (entity) =>
                entity.risks === 0
                    ? null
                    : (entity.assessedRisks * 100) / entity.risks;

            const stringCompare = Cmp.string();
            return {
                dataView: {
                    dataSource: new DV.ArrayDataSource<EntityAssignmentView>(
                        entities,
                        (entity) => entity.id
                    ),
                    extractorGroups: [
                        {
                            id: "standard",
                            label: "Standard",
                            extractors: [
                                {
                                    id: "name",
                                    label: t("RiskDirectory_Entity"),
                                    getValue: (entity) => entity.entityName,
                                    compare: Cmp.map(
                                        (entity) => entity.entityName,
                                        stringCompare
                                    ),
                                    asString: (entity) => entity.entityName,
                                },
                                {
                                    id: "risksTotal",
                                    label: t("RiskDirectory_Entity_Risks"),
                                    getValue: (entity) => entity.risks,
                                    compare: Cmp.map(
                                        (entity) => entity.risks,
                                        Cmp.primitive
                                    ),
                                    asString: (entity) =>
                                        entity.risks.toString(),
                                },
                                {
                                    id: "risksAssessed",
                                    label: t("RiskDirectory_Entity_Assessed"),
                                    getValue: (entity) => entity.assessedRisks,
                                    compare: Cmp.map(
                                        (entity) => entity.assessedRisks,
                                        Cmp.primitive
                                    ),
                                    asString: (entity) =>
                                        entity.assessedRisks.toString(),
                                },
                                {
                                    id: "status",
                                    label: t("RiskDirectory_Entity_Status"),
                                    getValue: getEntityStatus,
                                    compare: Cmp.map(
                                        getEntityStatus,
                                        Cmp.primitive
                                    ),
                                    asString: (entity) =>
                                        getEntityStatus(entity)?.toString() ??
                                        "0",
                                },
                            ],
                        },
                    ],
                    sorters: [],
                    defaultCompareItems: Cmp.map(
                        (control) => control.id,
                        stringCompare
                    ),
                },
                columnBuilders: {
                    name: (extractor) => ({
                        getFormattedValue: (entity) => (
                            <Link to={`${RISK_MODULE}/${item.id}/${entity.id}`}>
                                {extractor.asString(entity)}
                            </Link>
                        ),
                    }),
                    risksTotal: (extractor) => ({
                        getFormattedValue: (entity) => {
                            return <span>{extractor.asString(entity)}</span>;
                        },
                        layout: {
                            fixedWidth: FIXED_WIDTH_NARROW,
                        },
                    }),
                    risksAssessed: (extractor) => ({
                        getFormattedValue: (entity) => {
                            return <span>{extractor.asString(entity)}</span>;
                        },
                        layout: {
                            fixedWidth: FIXED_WIDTH_NARROW,
                        },
                    }),
                    status: (extractor) => ({
                        getFormattedValue: (entity) => {
                            const status = extractor.getValue(entity);
                            if (status === undefined || status === null) {
                                return <></>;
                            }
                            const percentFloored = Math.floor(Number(status));
                            return (
                                <div className={theme.entityStatus}>
                                    <ProgressBar percent={percentFloored} />
                                </div>
                            );
                        },
                        layout: {
                            fixedWidth: FIXED_WIDTH_WIDE * 2,
                        },
                    }),
                },
            };
        }
    );
    private renderExpandedRow = (
        item: RiskDirDto,
        _index: number,
        _indent: number,
        _expanded: boolean
    ): React.ReactNode => {
        const onExpand = this.expandRow;
        if (!onExpand) {
            return null;
        }
        const expanded = onExpand(item.resource);
        const headers = expanded.dataView.extractorGroups.map((group) => {
            return group.extractors.map((extractor) => {
                const builder = expanded.columnBuilders[extractor.id];
                let style;
                if (builder) {
                    const columnBuilder = builder(extractor);
                    style = columnBuilder.layout && {
                        width: `${columnBuilder.layout.fixedWidth}px`,
                    };
                }
                return (
                    <th
                        key={`${group.id}_${extractor.id}`}
                        style={style}
                        className={theme.expandedHeader}
                    >
                        {extractor.label}
                    </th>
                );
            });
        });
        const rows: Array<React.ReactNode> = [];
        DV.forEach(expanded.dataView.dataSource.iter(), (row) => {
            const rowId = expanded.dataView.dataSource.getId(row);
            rows.push(
                <tr key={`row_${rowId}`}>
                    {expanded.dataView.extractorGroups.map((group) => {
                        return group.extractors.map((extractor) => {
                            const builder =
                                expanded.columnBuilders[extractor.id];
                            let value: React.ReactNode;
                            let style;
                            if (builder) {
                                const columnBuilder = builder(extractor);
                                const format = columnBuilder.getFormattedValue;
                                if (format) {
                                    value = format(row);
                                }
                                style = columnBuilder.layout && {
                                    width: `${columnBuilder.layout.fixedWidth}px`,
                                };
                            }
                            if (!value) {
                                value = extractor.asString(row);
                            }
                            return (
                                <td
                                    key={`${group.id}_${extractor.id}`}
                                    style={style}
                                >
                                    {value}
                                </td>
                            );
                        });
                    })}
                </tr>
            );
        });
        return (
            <table className={theme.expandedTable}>
                <thead>
                    <tr>{headers}</tr>
                </thead>
                <tbody>{rows}</tbody>
            </table>
        );
    };

    private memoizeDataView: (
        riskDirectories: SimpleRiskDirectoryListDto
    ) => [DataView<RiskDirDto>, DataViewIndex<RiskDirDto>] = memoizeOne(
        (riskDirectories: SimpleRiskDirectoryListDto) => {
            const getRisksSum = (dto: RiskDirDto) =>
                Object.values(dto.resource.entities)
                    .map((e) => e.risks)
                    .reduce((a, b) => a + b, 0);

            const getAssessedSum = (dto: RiskDirDto) =>
                Object.values(dto.resource.entities)
                    .map((e) => e.assessedRisks)
                    .reduce((a, b) => a + b, 0);

            const getStatusSum = (dto: RiskDirDto) => {
                const risks = getRisksSum(dto);
                const assessed = getAssessedSum(dto);
                return risks === 0 ? null : (assessed * 100) / risks;
            };

            const stringCompare = Cmp.string();
            const extractors: DataExtractor<RiskDirDto, unknown>[] = [
                {
                    id: "title",
                    label: t("RiskDirectory_Manager_Label_Title"),
                    getValue: (dto: RiskDirDto) => dto.resource.title,
                    compare: Cmp.map(
                        (dto) => dto.resource.title,
                        stringCompare
                    ),
                    asString: (dto) => dto.resource.title,
                },
                {
                    id: "description",
                    label: t("RiskDirectory_Label_Description"),
                    getValue: (dto: RiskDirDto) => dto.resource.description,
                    compare: Cmp.map(
                        (dto) => dto.resource.description,
                        stringCompare
                    ),
                    asString: (dto) => dto.resource.description,
                },
                {
                    id: "entities",
                    label: t("RiskDirectory_Label_Entities"),
                    getValue: (dto: RiskDirDto) =>
                        Object.keys(dto.resource.entities),
                    compare: Cmp.map(
                        (dto) => Object.keys(dto.resource.entities).length,
                        Cmp.primitive
                    ),
                    asString: (dto) =>
                        Object.keys(dto.resource.entities).length.toString(),
                },
                {
                    id: "risksTotal",
                    label: t("RiskDirectory_Label_Risks"),
                    getValue: getRisksSum,
                    compare: Cmp.map((dto) => getRisksSum(dto), Cmp.primitive),
                    asString: (dto) => getRisksSum(dto).toString(),
                },
                {
                    id: "risksAssessed",
                    label: t("RiskDirectory_Label_Assessed"),
                    getValue: getAssessedSum,
                    compare: Cmp.map(
                        (dto) => getAssessedSum(dto),
                        Cmp.primitive
                    ),
                    asString: (dto) => getAssessedSum(dto).toString(),
                },
                {
                    id: "status",
                    label: t("RiskDirectory_Label_Status"),
                    getValue: getStatusSum,
                    compare: Cmp.map((dto) => getStatusSum(dto), Cmp.primitive),
                    asString: (dto) => getStatusSum(dto)?.toString() ?? "0",
                },
                {
                    id: "actions",
                    label: "",
                    getValue: (dto: RiskDirDto) => dto.resource.id,
                    compare: Cmp.map((dto) => dto.resource.id, Cmp.primitive),
                    asString: () => t("RiskManager_RiskDirectory"),
                },
            ];

            const getId = (dto: RiskDirDto) => dto.resource.id.toString();

            const dataView: DataView<RiskDirDto> = {
                dataSource: new ArrayDataSource(riskDirectories.content, getId),
                extractorGroups: [
                    {
                        id: "standard",
                        label: "Standard",
                        extractors,
                    },
                ],
                sorters: [],
                defaultCompareItems: Cmp.map(
                    (dto) => dto.resource.id,
                    Cmp.primitive
                ),
            };

            const expanders: DV.ExtractorExpander<Array<string>, string>[] = [
                {
                    extractorId: "entities",
                    expand: (entities) =>
                        entities.map((entityId) => {
                            const entitiesContent =
                                riskDirectories.linkedResources.entities;
                            const matchEntity = entitiesContent.find(
                                (entity) => entity.id.toString() === entityId
                            );
                            if (matchEntity === undefined) {
                                throw new Error(
                                    `Entity id ${entityId} not found in linked entity resources`
                                );
                            }
                            return {
                                partialId: `entity-${entityId}`,
                                expandedValue: matchEntity.name,
                            };
                        }),
                    asString: (item) => item,
                    compare: stringCompare,
                },
            ];

            return [
                dataView,
                new DataViewIndex(dataView, ["title", "entities"], expanders),
            ];
        }
    );

    private buildQueryResult = memoizeOne(
        (
            dataView: DataView<RiskDirDto>,
            dataViewIndex: DataViewIndex<RiskDirDto>,
            filter: SimpleAndFilter | null
        ): QueryResult<RiskDirDto> => {
            if (filter) {
                return dataViewIndex.find(filter);
            } else {
                return {
                    dataView,
                    orResults: [],
                    excludedItemIds: null,
                };
            }
        }
    );

    private addFilter = (
        extractorId: string,
        filter: SimpleFieldFilter,
        excluded: boolean
    ) => {
        this.setState({
            filter: addFieldFilter(
                extractorId,
                filter,
                excluded,
                this.state.filter
            ),
        });
    };

    private removeFilter = (
        extractorId: string,
        filter: SimpleFieldFilter,
        excluded: boolean
    ) => {
        if (this.state.filter) {
            this.setState({
                filter: removeFieldFilter(
                    extractorId,
                    filter,
                    excluded,
                    this.state.filter
                ),
            });
        }
    };

    private resetFilter = () => {
        this.setState({ filter: null });
    };

    renderContent = () => {
        const { riskDirectories } = this.props;

        if (riskDirectories.content.length === 0) {
            if (userCanCreateRiskDirectory(this.props.availableActions)) {
                return (
                    <NoContent
                        header={t("RiskManager_NoRiskDirectoriesAvailable")}
                        description={t(
                            "RiskManager_YouHaventCreatedRiskDirYet"
                        )}
                        // See app bar below
                        actionCreation={{
                            type: "ROUTER",
                            label: t("RiskDirectory_Creator_Title"),
                            to: NEW_RISK_DIRECTORY,
                        }}
                    />
                );
            } else {
                return (
                    <NoContent
                        header={t("RiskManager_NoRiskDirectoriesAvailable")}
                        description={t(
                            "RiskManager_IfYouNeedToCreateRiskDirAskForRole"
                        )}
                    />
                );
            }
        } else {
            const columnBuilders: Record<
                string,
                ColumnBuilder<RiskDirDto, any>
            > = {
                title: (extractor: DataExtractor<RiskDirDto, string>) => ({
                    getFormattedValue: (row: RiskDirDto) => {
                        const url = RISK_MODULE + "/" + row.resource.id;
                        const entitiesCount = Object.keys(
                            row.resource.entities
                        ).length;
                        if (entitiesCount > 0) {
                            return (
                                <Link to={url + RISK_MAP_HASH}>
                                    {extractor.asString(row)}
                                </Link>
                            );
                        } else {
                            return (
                                <Tooltip
                                    title={t(
                                        "RiskManager_NoEntitiesAssignedYet"
                                    )}
                                >
                                    <span className={theme.disabledLink}>
                                        {extractor.asString(row)}
                                    </span>
                                </Tooltip>
                            );
                        }
                    },
                    display: "DEFAULT_VISIBLE",
                    sortable: false,
                    layout: {
                        fixedWidth: FIXED_WIDTH_WIDE,
                    },
                }),
                description: (
                    extractor: DataExtractor<RiskDirDto, string>
                ) => ({
                    getFormattedValue: (row: RiskDirDto) =>
                        renderMarkdown(extractor.getValue(row)),
                    display: "DEFAULT_VISIBLE",
                    sortable: false,
                }),
                entities: (extractor: DataExtractor<RiskDirDto, number>) => ({
                    getFormattedValue: (row: RiskDirDto) => {
                        const entities = extractor.asString(row);
                        return <span>{entities}</span>;
                    },
                    display: "NEVER",
                    sortable: false,
                }),
                risksTotal: (extractor: DataExtractor<RiskDirDto, number>) => ({
                    getFormattedValue: (row: RiskDirDto) => {
                        const risks = extractor.asString(row);
                        return <span>{risks}</span>;
                    },
                    display: "DEFAULT_VISIBLE",
                    sortable: false,
                    layout: {
                        fixedWidth: FIXED_WIDTH_NARROW,
                    },
                }),
                risksAssessed: (
                    extractor: DataExtractor<RiskDirDto, number>
                ) => ({
                    getFormattedValue: (row: RiskDirDto) => {
                        const assessed = extractor.asString(row);
                        return <span>{assessed}</span>;
                    },
                    display: "DEFAULT_VISIBLE",
                    sortable: false,
                    layout: {
                        fixedWidth: FIXED_WIDTH_NARROW,
                    },
                }),
                status: (extractor: DataExtractor<RiskDirDto, number>) => ({
                    getFormattedValue: (row: RiskDirDto) => {
                        const status = extractor.getValue(row);
                        if (status === undefined || status === null) {
                            return <></>;
                        }
                        const percentFloored = Math.floor(Number(status));
                        return <ProgressBar percent={percentFloored} />;
                    },
                    display: "DEFAULT_VISIBLE",
                    sortable: false,
                    layout: {
                        fixedWidth: FIXED_WIDTH_WIDE,
                    },
                }),
                actions: () => ({
                    getFormattedValue: (row: RiskDirDto) => {
                        const url = RISK_MODULE + "/" + row.resource.id;
                        return (
                            <div className={theme.actions}>
                                <Link to={url + RISK_DIRECTORY_HASH}>
                                    {t("RiskManager_RiskDirectory")}
                                </Link>
                                <Link to={url + RISK_DIRECTORY_STRUCTURE}>
                                    {t("RiskDirectory_View_Action_Settings")}
                                </Link>
                            </div>
                        );
                    },
                    display: "DEFAULT_VISIBLE",
                    sortable: false,
                    layout: {
                        fixedWidth: FIXED_WIDTH_EXTRA_WIDE,
                    },
                }),
            };

            const { filter } = this.state;
            const [dataView, dataViewIndex] =
                this.memoizeDataView(riskDirectories);
            const queryResult = this.buildQueryResult(
                dataView,
                dataViewIndex,
                filter
            );
            const expandedRowRender = this.renderExpandedRow;
            return (
                <ContentWithoutBorder className={theme.table}>
                    <TableFilter<RiskDirDto>
                        id="rd-overview-filter"
                        queryResult={queryResult}
                        dataViewIndex={dataViewIndex}
                        filter={filter}
                        extractorRefs={getAllExtractors(dataView).map(
                            (ext) => ({
                                extractorId: ext.id,
                            })
                        )}
                        onAddFilter={this.addFilter}
                        onRemoveFilter={this.removeFilter}
                        onResetFilter={this.resetFilter}
                    />
                    <DataTable
                        id="rd-overview"
                        mode={DATA_TABLE_MODE_VIEWPORT}
                        dataView={queryResult.dataView}
                        columnBuilders={columnBuilders}
                        expandedRowRender={expandedRowRender}
                        expandedRowClassName={() => theme.expandedRow}
                    />
                </ContentWithoutBorder>
            );
        }
    };
}

interface RiskManagerEntitiesProps {
    entities: EntityOverviewListDto;
    ajaxManager: AjaxManager;
    availableActions: AvailableActions;
    showEntityManagementNotice: boolean;
}

interface FilterState {
    dataView: DataView<EntityOverview>;
    dataViewIndex: DataViewIndex<EntityOverview>;
    filter: SimpleAndFilter | null;
    queryResult: QueryResult<EntityOverview>;
}

interface RiskManagerEntitiesState {
    filterState: FilterState;
}

class RiskManagerEntities extends React.PureComponent<
    RiskManagerEntitiesProps,
    RiskManagerEntitiesState
> {
    constructor(props: RiskManagerEntitiesProps) {
        super(props);
        const { entities } = props;
        const dataView = this.buildDataView(entities);
        const dataViewIndex = this.buildDataViewIndex(dataView);
        this.state = {
            filterState: {
                dataView,
                dataViewIndex,
                filter: null,
                queryResult: {
                    dataView,
                    orResults: [],
                    excludedItemIds: null,
                },
            },
        };
    }

    render = () => {
        const { ajaxManager, availableActions, showEntityManagementNotice } =
            this.props;
        return (
            <RiskManagerApplicationBar
                currentTabId={ENTITIES_TAB}
                ajaxManager={ajaxManager}
            >
                {showEntityManagementNotice && (
                    <EntityManagementNotice
                        canCreateEntities={
                            availableActions.controlEntities.create
                        }
                    />
                )}
                {this.renderContent()}
            </RiskManagerApplicationBar>
        );
    };

    renderContent = () => {
        const { entities } = this.props;

        if (entities.content.length === 0) {
            if (userCanCreateEntity(this.props.availableActions)) {
                return (
                    <NoContent
                        header={t("RiskManager_NoEntitiesAvailable")}
                        description={t("RiskManager_YouHaventCreatedEntityYet")}
                        // See app bar below
                        actionCreation={{
                            type: "ROUTER",
                            label: t("RiskEntities_Create_Title"),
                            to: NEW_ENTITY,
                        }}
                    />
                );
            } else {
                return (
                    <NoContent
                        header={t("RiskManager_NoEntitiesAvailable")}
                        description={t(
                            "RiskManager_IfYouNeedToCreateEntityAskForRole"
                        )}
                    />
                );
            }
        } else {
            const filteredEntities = toArray(
                this.state.filterState.queryResult.dataView.dataSource.iter()
            );

            const rows = filteredEntities.map((entity) => {
                return {
                    key: entity.id,
                    name: (
                        <Link to={WebRoute.RISK_ENTITIES + "/" + entity.id}>
                            {entity.name}
                        </Link>
                    ),
                    description: renderMarkdown(entity.description),
                };
            });

            const columns = [
                {
                    title: t("RiskMapSettings_EntityName"),
                    dataIndex: "name",
                    key: "name",
                    className: dataTableTheme.header,
                },
                {
                    title: t("RiskMapSettings_EntityDescription"),
                    dataIndex: "description",
                    key: "description",
                    className: dataTableTheme.header,
                },
            ];

            return (
                <ContentWithoutBorder>
                    <TableFilter<EntityOverview>
                        id="rd-entities-filter"
                        queryResult={this.state.filterState.queryResult}
                        dataViewIndex={this.state.filterState.dataViewIndex}
                        filter={this.state.filterState.filter}
                        hideResultCount={false}
                        extractorRefs={getAllExtractors(
                            this.state.filterState.dataView
                        ).map((ext) => ({ extractorId: ext.id }))}
                        onAddFilter={this.updateFilter}
                        onRemoveFilter={this.removeFilter}
                        onResetFilter={this.removeAllFilters}
                    />
                    <PaginatedTable
                        className={theme.table}
                        dataSource={rows}
                        columns={columns}
                        pagination={false}
                        bordered
                    />
                </ContentWithoutBorder>
            );
        }
    };

    private buildDataView = (
        entities: EntityOverviewListDto
    ): DataView<EntityOverview> => {
        const stringCompare = Cmp.string();
        const sortedEntities = entities.content.map(
            (content) => content.resource
        );
        sortedEntities.sort(Cmp.map((row) => row.name, stringCompare));
        const extractors: Array<DataExtractor<EntityOverview, any>> = [
            {
                id: "name",
                label: t("RiskMapSettings_EntityName"),
                getValue: (row) => row.name,
                compare: Cmp.map((row) => row.name, stringCompare),
                asString: (row) => row.name,
            },
        ];
        return {
            dataSource: new ArrayDataSource(sortedEntities, (row) =>
                row.id.toString()
            ),
            extractorGroups: [
                {
                    id: "standard",
                    label: "Standard",
                    extractors,
                },
            ],
            sorters: [
                {
                    extractorId: "name",
                    direction: "ASC",
                },
            ],
            defaultCompareItems: Cmp.map((row) => row.name, stringCompare),
        };
    };

    private buildDataViewIndex = (
        dataView: DataView<EntityOverview>
    ): DataViewIndex<EntityOverview> => {
        return new DataViewIndex(dataView);
    };

    private updateFilter = (
        extractorId: string,
        fieldFilter: SimpleFieldFilter,
        excluded: boolean
    ) => {
        const oldFilter = this.state.filterState.filter;
        const filter = addFieldFilter(
            extractorId,
            fieldFilter,
            excluded,
            oldFilter
        );
        this.updateDataBasedOnFilter({ ...this.state.filterState, filter });
    };

    private removeFilter = (
        extractorId: string,
        fieldFilter: SimpleFieldFilter,
        excluded: boolean
    ) => {
        const oldFilter = this.state.filterState.filter;
        if (oldFilter) {
            const filter = removeFieldFilter(
                extractorId,
                fieldFilter,
                excluded,
                oldFilter
            );
            this.updateDataBasedOnFilter({ ...this.state.filterState, filter });
        }
    };

    private removeAllFilters = () => {
        this.updateDataBasedOnFilter({
            ...this.state.filterState,
            filter: null,
        });
    };

    private updateDataBasedOnFilter = (filterState: FilterState) => {
        const filter = filterState.filter;
        const queryResult = filterState.dataViewIndex.find(filter);
        const dataViewIndex = filterState.dataViewIndex;
        this.setState({
            filterState: {
                dataView: queryResult.dataView,
                dataViewIndex,
                filter,
                queryResult,
            },
        });
    };
}

export const RiskManager = FormCreate<object, RiskManagerFormProps>()(
    withI18n<RiskManagerFormProps>(RiskManagerForm)
);
